import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getApplicationUser, ApplicationUser } from '../api/adminApi';

interface AdminContextType {
    isAdmin: boolean;
    isLoading: boolean;
    applicationUser: ApplicationUser | null;
}

const AdminContext = createContext<AdminContextType>({
    isAdmin: false,
    isLoading: true,
    applicationUser: null
});

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [applicationUser, setApplicationUser] = useState<ApplicationUser | null>(null);

    useEffect(() => {
        const fetchAdminStatus = async () => {
            if (!isAuthenticated || isAuthLoading) {
                setIsAdmin(false);
                setIsLoading(false);
                setApplicationUser(null);
                return;
            }

            try {
                const user = await getApplicationUser();
                setApplicationUser(user);
                setIsAdmin(user.isAdmin);
            } catch (error) {
                console.error('Error fetching admin status:', error);
                setIsAdmin(false);
                setApplicationUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAdminStatus();
    }, [isAuthenticated, isAuthLoading]);

    return (
        <AdminContext.Provider value={{ isAdmin, isLoading, applicationUser }}>
            {children}
        </AdminContext.Provider>
    );
};
